// dlc-controller.ts — the HTML/DOM ADD-ON MISSION PACKS flow, LOGIC half
// (HTML/DOM UI shell plan, Phase 3). A headless screen state machine that
// drives the SAME ppim/packm/HttpIndexLoader machinery the canvas
// DownloadMissionPackForm + DownloadBox drive (game/dlmissionpack.cpp +
// game/downloadbox.cpp, ported in ui/dlc-forms.ts) — so the catalog it lists,
// the install it performs, and the done(packid|null) it hands back are the
// SAME as the canvas form path. No DOM here — dlc-view.ts renders this state
// and routes clicks back to these methods; pure-data state for node-testing
// against an injected fake HttpService (test/client/dom-dlc.test.ts).
//
// Reuses unchanged: HttpIndexLoader (catalog fetch + the Add-On form's
// sorting/merging), HttpPackManager (download/install/IsInstalled/EnumPacks/
// Remove), HttpPackInfoManager (the pack-info JSON), CreateMissionList (the
// installed-count message), and the DlcEnv-shaped deps bundle main.ts already
// builds in boot(). The C++ ProgressCallback (index/info/pack download) lands
// on the JS event loop here, exactly like the form path.

import type { PackId } from '../io/savegame.ts';
import type { HttpService, ProgressCallback } from '../net/httpservice.ts';
import type { HttpPackManager } from '../net/httppackmanager.ts';
import type { HttpPackInfoManager } from '../net/httppackinfomanager.ts';
import { forumDiscussUrl } from '../net/httppackinfomanager.ts';
import {
  HttpIndexLoader,
  type IndexEntry,
  type SortType,
  SORT_UNSORTED,
  SORT_INSTALLEDASCENDING,
  SORT_INSTALLEDDESCENDING,
  SORT_TITLEASCENDING,
  SORT_TITLEDESCENDING,
  SORT_PLAYERSASCENDING,
  SORT_PLAYERSDESCENDING,
  SORT_MISSIONSASCENDING,
  SORT_MISSIONSDESCENDING,
} from '../net/httpindexloader.ts';
import { CreateMissionList, kmltAll, type MissionListDeps } from '../ui/missionpick.ts';

// game/dlmissionpack.cpp:12-13 — the ProgressCallback ctx discriminators
const CTX_INDEX = 0;
const CTX_INFO = 1;

// game/dlmissionpack.cpp:15 — the fake Custom URL catalog row's pack id
export const PACKID_CUSTOMURL = 0xfefefefe;

/** The C++ DLC globals bundle (gphttp/gppackm/gppim + the CreateMissionList
 *  deps) — the SAME slice of DlcEnv (ui/dlc-forms.ts) the DOM controller needs;
 *  main.ts hands it the boot()-built http/packm/ppim/mlDeps. */
export interface DlcControllerDeps {
  /** gphttp (game/httpservice.h). */
  http: HttpService | null;
  /** gppackm (game/httppackmanager.h:49) — download + install + IsInstalled. */
  packm: HttpPackManager;
  /** gppim (game/httppackinfomanager.h:48) — pack-info JSON. */
  ppim: HttpPackInfoManager;
  /** the CreateMissionList globals bundle (gpakr/gppackm/gppim). */
  mlDeps: MissionListDeps;
  /** re-render on every state change. */
  onChange: () => void;
  /** HostOpenUrl (window.open) — DISCUSS opens the pack's forum/discuss URL. */
  openUrl?: (url: string) => void;
  /** REPORT (community-content-plan §4.4 / M2) — flag a community pack to the moderator
   *  queue (POST /api/reportpack, anonymous-with-did). main.ts owns the network side;
   *  absent ⇒ no REPORT button (e.g. a headless harness). */
  reportPack?: (packid: PackId) => void;
  /** the remembered Custom URL (prefs kszPrefAskUrl; dlmissionpack.cpp:715) —
   *  pre-fills the ask input. Absent ⇒ the input starts empty. */
  askUrlDefault?: () => string;
  /** persist the entered Custom URL (prefs.SetString(kszPrefAskUrl) +
   *  savePreferences; dlmissionpack.cpp:684-686). Absent ⇒ no persistence. */
  saveAskUrl?: (url: string) => void;
  /** the modal flow's terminal callback: done(packid) = PLAY the freshly
   *  downloaded pack (the C++ WantsPlay true), done(null) = DONE/back. */
  done: (packid: PackId | null) => void;
}

export type DlcScreen = 'chooser' | 'catalog' | 'download';

/** A catalog list row the view renders (the DownloadMissionPackForm columns:
 *  status marker / title / # players / # missions). */
export interface DlcRow {
  /** stable key into the loader's index (the PackId id; the view round-trips
   *  it back to select()). */
  key: string;
  /** install status: ' ' not-installed, '+' installed/current, '!' upgrade. */
  status: string;
  title: string;
  players: string; // "N" or "N-M"
  missions: string; // "N" (blank for the Custom URL row)
  /** true for the fake " Custom URL..." entry (no players/missions columns). */
  custom: boolean;
}

const clonePackId = (p: PackId): PackId => ({ id: p.id >>> 0, hash: p.hash.slice() });

const packIdEq = (a: PackId, b: PackId): boolean =>
  a.id >>> 0 === b.id >>> 0 && a.hash.every((x, i) => x === (b.hash[i] ?? 0));

/** The DOM ADD-ON MISSION PACKS controller — the headless twin of
 *  DownloadMissionPackForm + DownloadBox (ui/dlc-forms.ts). */
export class DlcController implements ProgressCallback {
  screen: DlcScreen = 'chooser';
  /** the catalog's status line (Contacting Server… / Download error: … / a
   *  download-progress message), shown in the list or below it. */
  catalogStatus = '';
  /** the selected pack's info label ("By <author>: <desc>" or a loading/error
   *  hint), the DownloadMissionPackForm kidcMissionPackInfo. */
  infoLabel = '';

  // the DownloadBox (modal overlay) state
  /** the box's title ("Downloading <pack>"). */
  downloadTitle = '';
  /** the box's status line (Starting download… / Downloaded N of M bytes / the
   *  completion or error message). */
  downloadStatus = '';
  /** the download finished (offer OK + PLAY); false = still downloading
   *  (CANCEL only). */
  downloadDone = false;
  /** the download errored (PLAY is never offered). */
  downloadError = false;
  /** a pending REMOVE awaiting the "Are you sure?" confirm overlay (the canvas
   *  HtMessageBox query; dlmissionpack.cpp:647-660). Null = no confirm pending. */
  pendingRemove: PackId | null = null;

  /** community pack ids the player has REPORTED this session — the REPORT button
   *  then reads "Reported" and stops firing (no per-session spam). */
  private reportedIds = new Set<number>();

  private readonly d: DlcControllerDeps;
  private idxl_: HttpIndexLoader | null = null;
  private fMultiplayer_ = false;
  private sort_: SortType = SORT_UNSORTED;
  /** the view-selected catalog row's pack id (null = nothing selected). */
  private selectedId: number | null = null;
  /** the in-flight info request's pack id, so a late OnFinished only paints if
   *  the same row is still selected. */
  private infoRequestId: number | null = null;
  /** the pack the DownloadBox is installing (the DownloadMissionPack arm). For a
   *  custom-URL download this is the placeholder the resolved id is written into. */
  private downloadPackId: PackId | null = null;
  /** true while the in-flight download is a custom-URL install (the resolved pack
   *  hash isn't known until FinishInstall writes it back into downloadPackId). */
  private downloadFromUrl = false;
  /** which callback the next ProgressCallback wave belongs to. */
  private progressCtx: typeof CTX_INDEX | typeof CTX_INFO | 'pack' = CTX_INDEX;

  constructor(d: DlcControllerDeps) {
    this.d = d;
  }

  // ── the chooser (kidfAddOnSingleMulti) ───────────────────────────────────────
  /** SINGLE PLAYER MISSION PACKS → the catalog filtered to single-player packs. */
  chooseSinglePlayer(): void {
    this.openCatalog(false);
  }
  /** MULTIPLAYER MISSION PACKS → the catalog filtered to multiplayer packs. */
  chooseMultiplayer(): void {
    this.openCatalog(true);
  }

  // dlmissionpack.cpp:116-152 — Init: start the index fetch, show "Contacting
  // Server...", no operation available until a row is selected
  private openCatalog(fMultiplayer: boolean): void {
    this.fMultiplayer_ = fMultiplayer;
    this.screen = 'catalog';
    this.catalogStatus = 'Contacting Server...';
    this.infoLabel = '';
    this.selectedId = null;
    this.infoRequestId = null;
    this.sort_ = SORT_UNSORTED;
    this.idxl_ = new HttpIndexLoader(this.d.http, this.d.packm, (ppackid, mlt) =>
      CreateMissionList(this.d.mlDeps, ppackid, mlt),
    );
    this.progressCtx = CTX_INDEX;
    if (!this.idxl_.Start(CTX_INDEX, this)) {
      this.catalogStatus = 'Could not contact the server.';
    }
    this.d.onChange();
  }

  // ── observable catalog state ─────────────────────────────────────────────────
  isMultiplayer(): boolean {
    return this.fMultiplayer_;
  }

  /** the filtered, sorted catalog rows (dlmissionpack.cpp:457-510 ShowIndex). */
  rows(): DlcRow[] {
    const idxl = this.idxl_;
    if (idxl === null) return [];
    const out: DlcRow[] = [];
    for (let i = 0; i < idxl.GetCount(); i++) {
      const e = idxl.GetEntry(i)!;
      if (e.packid.id >>> 0 !== PACKID_CUSTOMURL) {
        // single vs multiplayer filter; a pack spanning both shows in both
        if (this.fMultiplayer_) {
          if (e.cPlayersMax === 1) continue;
        } else {
          if (e.cPlayersMin !== 1) continue;
        }
      }
      out.push(this.rowFor(e));
    }
    return out;
  }

  private rowFor(e: IndexEntry): DlcRow {
    const custom = e.packid.id >>> 0 === PACKID_CUSTOMURL;
    let status = ' ';
    switch (this.d.packm.IsInstalled(e.packid)) {
      case 1:
        status = '+';
        break; // installed, current
      case 2:
        status = '!';
        break; // installed, upgrade available
      default:
        status = ' ';
        break; // not installed
    }
    const players =
      e.cPlayersMin === e.cPlayersMax ? `${e.cPlayersMin}` : `${e.cPlayersMin}-${e.cPlayersMax}`;
    return {
      key: String(e.packid.id >>> 0),
      status: custom ? ' ' : status,
      title: e.title,
      players: custom ? '' : players,
      missions: custom ? '' : `${e.cMissions}`,
      custom,
    };
  }

  /** the DOWNLOAD/REMOVE/UPGRADE action label for the selection, or null when no
   *  row is selected (dlmissionpack.cpp:567-607 HideShow → the button is hidden). */
  actionLabel(): string | null {
    const e = this.selectedEntry();
    if (e === null) return null;
    switch (this.d.packm.IsInstalled(e.packid)) {
      case 0:
        return 'DOWNLOAD';
      case 1:
        return 'REMOVE';
      case 2:
        return 'UPGRADE';
      default:
        return null;
    }
  }

  /** DISCUSS is shown only once the selected pack's info (with a "di" URL) is
   *  cached (dlmissionpack.cpp:505-542 ShowInfo). */
  canDiscuss(): boolean {
    const e = this.selectedEntry();
    if (e === null) return false;
    const map = this.d.ppim.GetInfoMap(e.packid);
    return map !== null && typeof map['di'] === 'string';
  }

  private selectedEntry(): IndexEntry | null {
    if (this.idxl_ === null || this.selectedId === null) return null;
    for (let i = 0; i < this.idxl_.GetCount(); i++) {
      const e = this.idxl_.GetEntry(i)!;
      if (e.packid.id >>> 0 === this.selectedId) return e;
    }
    return null;
  }

  // dlmissionpack.cpp:560-607 — selecting a row: load + show its info, set the
  // action button label
  select(key: string): void {
    this.selectedId = Number(key) >>> 0;
    this.showInfo(true);
    this.d.onChange();
  }

  // dlmissionpack.cpp:505-542 ShowInfo — paint the info label; request the pack
  // info if it isn't cached yet (skips the fake Custom URL row)
  private showInfo(fRequest: boolean): void {
    const e = this.selectedEntry();
    if (e === null) {
      this.infoLabel = '';
      return;
    }
    const map = this.d.ppim.GetInfoMap(e.packid);
    if (map === null) {
      if (fRequest) {
        if (e.packid.id >>> 0 === PACKID_CUSTOMURL) {
          this.infoLabel = 'Press Download to download from a custom URL';
        } else {
          this.infoRequestId = e.packid.id >>> 0;
          this.progressCtx = CTX_INFO;
          this.d.ppim.Start(e.packid, CTX_INFO, this);
          this.infoLabel = 'Loading Mission Pack info...';
        }
      }
      return;
    }
    const author = this.infoString(map, 'a', 'unknown');
    const desc = this.infoString(map, 'd', 'no description');
    this.infoLabel = `By ${author}: ${desc}`;
  }

  // dlmissionpack.cpp:537-558 — whitespace-only counts as missing
  private infoString(map: Record<string, unknown>, key: string, missing: string): string {
    const obj = map[key];
    if (typeof obj !== 'string' || obj.trim().length === 0) return missing;
    return obj;
  }

  // ── DISCUSS (dlmissionpack.cpp:664-673) ──────────────────────────────────────
  discuss(): void {
    const e = this.selectedEntry();
    if (e === null) return;
    const map = this.d.ppim.GetInfoMap(e.packid);
    const url = map?.['di'];
    // The stored "di" links still point at the dead xyzzy phpBB board; remap to
    // the read-only forum clone (issue #106).
    if (typeof url === 'string') this.d.openUrl?.(forumDiscussUrl(url));
  }

  // ── REPORT (community-content-plan §4.4 / M2) ────────────────────────────────
  // Shown for a real (non-Custom-URL) community pack once its info has loaded and a
  // reportPack seam is wired. One report per pack per session — after firing, the
  // button reads "Reported" (canReport → false).
  canReport(): boolean {
    if (this.d.reportPack === undefined) return false;
    const e = this.selectedEntry();
    if (e === null || e.packid.id >>> 0 === PACKID_CUSTOMURL) return false;
    if (this.reportedIds.has(e.packid.id >>> 0)) return false;
    // require the info to have loaded (so it's a real, resolvable pack)
    return this.d.ppim.GetInfoMap(e.packid) !== null;
  }

  reportPack(): void {
    const e = this.selectedEntry();
    if (e === null || this.d.reportPack === undefined) return;
    const id = e.packid.id >>> 0;
    if (id === PACKID_CUSTOMURL || this.reportedIds.has(id)) return;
    this.reportedIds.add(id);
    this.d.reportPack(clonePackId(e.packid));
    this.infoLabel = 'Reported to the moderators. Thanks!';
    this.d.onChange();
  }

  // ── the action button (DOWNLOAD / REMOVE / UPGRADE; dlmissionpack.cpp:609-675) ─
  action(): void {
    const e = this.selectedEntry();
    if (e === null) return;
    const nInstalled = this.d.packm.IsInstalled(e.packid);

    if (nInstalled === 0 && e.packid.id >>> 0 === PACKID_CUSTOMURL) {
      // the fake Custom URL row → ask for a URL, then InstallFromUrl
      // (dlmissionpack.cpp:625-627 HandleCustomURL → InitiateAsk)
      this.pendingCustomUrl = true;
      this.customUrl = this.d.askUrlDefault?.() ?? '';
      this.d.onChange();
      return;
    }

    if (nInstalled === 1) {
      // installed → REMOVE, gated by the canvas "Are you sure?" query
      // (dlmissionpack.cpp:647-660). The view renders the confirm overlay.
      this.pendingRemove = clonePackId(e.packid);
      this.d.onChange();
      return;
    }
    // not installed or needs upgrade → download
    this.startDownload(clonePackId(e.packid));
  }

  // ── REMOVE confirmation (the canvas HtMessageBox 'Are you sure?' query) ───────
  /** YES on the confirm overlay → actually uninstall the pending pack. */
  confirmRemove(): void {
    const packid = this.pendingRemove;
    this.pendingRemove = null;
    if (packid === null) return;
    this.removeSelected(packid);
  }
  /** NO on the confirm overlay → dismiss without removing. */
  cancelRemove(): void {
    this.pendingRemove = null;
    this.d.onChange();
  }

  /** REMOVE a pack (called only after the confirm; dlmissionpack.cpp:652-658). */
  removeSelected(packid?: PackId): void {
    const target = packid ?? this.selectedEntry()?.packid ?? null;
    if (target === null) return;
    if (this.d.packm.IsInstalled(target) !== 1) return;
    this.d.packm.Remove(target);
    if (this.idxl_!.OnRemoved(target)) {
      // a generated (not-in-index) row was dropped → re-filter; keep selection
      // valid by clearing it if it's gone
      if (this.selectedEntry() === null) this.selectedId = null;
    }
    this.showInfo(false);
    this.d.onChange();
  }

  // ── Custom URL (dlmissionpack.cpp:679-717) ───────────────────────────────────
  /** the entered custom URL while the ask overlay is open (the view's input). */
  customUrl = '';
  /** true while the Custom URL ask overlay is up (the view renders the input). */
  pendingCustomUrl = false;

  /** the view updates this as the user types into the URL input. */
  setCustomUrl(url: string): void {
    this.customUrl = url;
  }

  /** CANCEL on the Custom URL ask overlay → dismiss. */
  cancelCustomUrl(): void {
    this.pendingCustomUrl = false;
    this.customUrl = '';
    this.d.onChange();
  }

  /** OK on the Custom URL ask overlay → persist the URL (like the canvas
   *  kszPrefAskUrl) and download it via packm.InstallFromUrl
   *  (dlmissionpack.cpp:683-709 + downloadbox.cpp:838-839 the URL DownloadBox). */
  submitCustomUrl(): void {
    const url = this.customUrl.trim();
    this.pendingCustomUrl = false;
    if (url === '') {
      this.customUrl = '';
      this.d.onChange();
      return;
    }
    // persist so this URL is remembered (the canvas prefs.SetString +
    // savePreferences). The host wires saveAskUrl when prefs are available.
    this.d.saveAskUrl?.(url);
    this.startCustomUrlDownload(url);
  }

  // downloadbox.cpp:113-116 (the URL ctor) + 838-839 — the DownloadBox over a
  // custom URL: title IS the URL, fPlayButton true, InstallFromUrl (the pack
  // hash is unknown until FinishInstall resolves it and writes it back here).
  private startCustomUrlDownload(url: string): void {
    // the resolved pack id lands here (httppackmanager FinishInstall writes the
    // adopted id+hash back through ppackidUpdate) so PLAY/the list can use it.
    const resolved: PackId = { id: 0, hash: new Uint8Array(16) };
    this.downloadPackId = resolved;
    this.downloadFromUrl = true;
    this.downloadDone = false;
    this.downloadError = false;
    this.screen = 'download';
    this.downloadTitle = `Downloading ${url}`;
    this.downloadStatus = 'Starting download...';
    this.progressCtx = 'pack';
    this.d.onChange();
    if (!this.d.packm.InstallFromUrl(url, resolved, null, this)) {
      this.downloadError = true;
      this.downloadDone = true;
      this.downloadStatus = 'Could not start the download.';
      this.d.onChange();
    }
  }

  // ── the DownloadBox (downloadbox.cpp; DownloadMissionPack) ────────────────────
  private startDownload(packid: PackId): void {
    this.downloadPackId = packid;
    this.downloadFromUrl = false;
    this.downloadDone = false;
    this.downloadError = false;
    this.screen = 'download';
    // the title prefers the cached info "t" (downloadbox.cpp:122-135)
    const map = this.d.ppim.GetInfoMap(packid);
    const title = typeof map?.['t'] === 'string' ? (map['t'] as string) : null;
    this.downloadTitle = title !== null ? `Downloading ${title}` : 'Downloading...';
    this.downloadStatus = 'Starting download...';
    this.progressCtx = 'pack';
    this.d.onChange();
    if (!this.d.packm.Install(packid, null, this)) {
      this.downloadError = true;
      this.downloadDone = true;
      this.downloadStatus = 'Could not start the download.';
      this.d.onChange();
    }
  }

  /** OK on the DownloadBox: return to the catalog with the new install
   *  reflected (downloadbox.cpp's nResult != kidcPlay). */
  downloadOk(): void {
    const resolved = this.downloadPackId;
    const fromUrl = this.downloadFromUrl;
    this.d.packm.Cancel(); // ~DownloadBox cancels any in-flight request
    this.screen = 'catalog';
    // a successful custom-URL install: add the resolved pack to the index and
    // select it (dlmissionpack.cpp:704-708 the OnFilterEvent non-play arm).
    if (fromUrl && !this.downloadError && resolved !== null && this.idxl_ !== null) {
      this.idxl_.AddIndexEntry(resolved);
      this.idxl_.Sort(this.sort_);
      this.selectedId = resolved.id >>> 0;
    }
    // re-select so the status marker + action flip to the installed state
    if (this.selectedId !== null) this.showInfo(false);
    this.downloadPackId = null;
    this.downloadFromUrl = false;
    this.d.onChange();
  }

  /** PLAY on the DownloadBox: hand the freshly downloaded pack back through the
   *  done seam (the C++ WantsPlay true → ShowDownloadMissionPackForm done). */
  downloadPlay(): void {
    const packid = this.downloadPackId;
    this.d.packm.Cancel();
    this.downloadPackId = null;
    this.downloadFromUrl = false;
    this.d.done(packid !== null ? clonePackId(packid) : null);
  }

  // ── BACK / DONE ──────────────────────────────────────────────────────────────
  // dlmissionpack.cpp:723-763 ShowDownloadMissionPackForm — the single/multi
  // chooser (kidfAddOnSingleMulti) runs ONCE up front; after it picks SP/MP the
  // catalog form (kidfDownloadMissionPackWide) opens, and that form's DONE
  // (kidcCancel) ends the whole flow → done(null) back to the MENU. The catalog
  // never loops back to the chooser, so there is no "back to chooser" path.
  /** BACK from the chooser, or DONE from the catalog → unwind to the menu. */
  done(): void {
    this.d.done(null);
  }

  // ── ProgressCallback (index / info / pack download) ──────────────────────────
  OnBegin(ctx: unknown, cbLength: number): void {
    this.OnProgress(ctx, 0, cbLength);
  }

  OnProgress(ctx: unknown, cbTotal: number, cbLength: number): void {
    if (ctx === CTX_INDEX) {
      this.catalogStatus = `Downloaded ${cbTotal} of ${cbLength} bytes...`;
      this.d.onChange();
    } else if (this.progressCtx === 'pack') {
      this.downloadStatus = `Downloaded ${cbTotal} of ${cbLength} bytes`;
      this.d.onChange();
    }
    // CTX_INFO progress is silent (the canvas only shows the loading label)
  }

  OnError(ctx: unknown, pszError: string): void {
    if (ctx === CTX_INFO) {
      // info errors are ignored (it's ok to have no info) — dlmissionpack.cpp:378
      this.infoLabel = '';
      this.d.onChange();
      return;
    }
    if (ctx === CTX_INDEX) {
      this.catalogStatus = `Download error: ${pszError}`;
      this.d.onChange();
      return;
    }
    // pack download error (downloadbox.cpp:241-246)
    this.downloadError = true;
    this.downloadDone = true;
    this.downloadStatus = `Download error: ${pszError}`;
    this.d.onChange();
  }

  OnFinished(ctx: unknown): void {
    if (ctx === CTX_INDEX) {
      this.onIndexFinished();
      return;
    }
    if (ctx === CTX_INFO) {
      // re-paint the info label only if the request's row is still selected
      if (this.infoRequestId !== null && this.selectedId === this.infoRequestId) {
        this.showInfo(false);
      }
      this.infoRequestId = null;
      this.d.onChange();
      return;
    }
    // pack download finished (downloadbox.cpp:183-239)
    this.onDownloadFinished();
  }

  // dlmissionpack.cpp:389-419 OnFinished(CTX_INDEX): merge installed packs, add
  // the fake Custom URL row, default-sort by missions, select the first row
  private onIndexFinished(): void {
    const idxl = this.idxl_;
    if (idxl === null) return;
    this.catalogStatus = '';
    idxl.MergeInstalled(this.d.packm);
    const customId: PackId = { id: PACKID_CUSTOMURL, hash: new Uint8Array(16) };
    idxl.AddFakeEntry(customId, ' Custom URL...', 999, 999, 0);
    this.applyDefaultSort();
    // select the first visible row (the canvas Select(iSelect))
    const rows = this.rows();
    if (rows.length > 0) this.select(rows[0].key);
    else this.d.onChange();
  }

  // dlmissionpack.cpp:252-338 — the default per-column sort the catalog applies
  // on first load is missions-descending
  private applyDefaultSort(): void {
    this.sort_ = SORT_MISSIONSDESCENDING;
    this.idxl_!.Sort(this.sort_);
  }

  /** the active sort column (the DownloadMissionPackForm idSortIndicator_ — the
   *  header that carries the '*' marker), or null when unsorted. The view appends
   *  the canvas '*' to this column's header (dlmissionpack.cpp:304-321). */
  sortColumn(): 'status' | 'title' | 'players' | 'missions' | null {
    switch (this.sort_) {
      case SORT_INSTALLEDASCENDING:
      case SORT_INSTALLEDDESCENDING:
        return 'status';
      case SORT_TITLEASCENDING:
      case SORT_TITLEDESCENDING:
        return 'title';
      case SORT_PLAYERSASCENDING:
      case SORT_PLAYERSDESCENDING:
        return 'players';
      case SORT_MISSIONSASCENDING:
      case SORT_MISSIONSDESCENDING:
        return 'missions';
      default:
        return null;
    }
  }

  /** the active sort direction ('asc'/'desc'), or null when unsorted (view hint). */
  sortDirection(): 'asc' | 'desc' | null {
    switch (this.sort_) {
      case SORT_INSTALLEDASCENDING:
      case SORT_TITLEASCENDING:
      case SORT_PLAYERSASCENDING:
      case SORT_MISSIONSASCENDING:
        return 'asc';
      case SORT_INSTALLEDDESCENDING:
      case SORT_TITLEDESCENDING:
      case SORT_PLAYERSDESCENDING:
      case SORT_MISSIONSDESCENDING:
        return 'desc';
      default:
        return null;
    }
  }

  /** sort the catalog by a column (the DownloadMissionPackForm header-tap sort).
   *  Re-tapping the active column flips the direction. */
  sortBy(column: 'status' | 'title' | 'players' | 'missions'): void {
    if (this.idxl_ === null) return;
    const asc: Record<string, SortType> = {
      status: SORT_INSTALLEDASCENDING,
      title: SORT_TITLEASCENDING,
      players: SORT_PLAYERSASCENDING,
      missions: SORT_MISSIONSASCENDING,
    };
    const desc: Record<string, SortType> = {
      status: SORT_INSTALLEDDESCENDING,
      title: SORT_TITLEDESCENDING,
      players: SORT_PLAYERSDESCENDING,
      missions: SORT_MISSIONSDESCENDING,
    };
    // the per-column default direction (dlmissionpack.cpp:328-336):
    // installed-desc, title-asc, players-asc, missions-desc
    const dflt: Record<string, SortType> = {
      status: SORT_INSTALLEDDESCENDING,
      title: SORT_TITLEASCENDING,
      players: SORT_PLAYERSASCENDING,
      missions: SORT_MISSIONSDESCENDING,
    };
    const next =
      this.sort_ === asc[column]
        ? desc[column]
        : this.sort_ === desc[column]
          ? asc[column]
          : dflt[column];
    this.sort_ = next;
    this.idxl_.Sort(next);
    this.d.onChange();
  }

  // downloadbox.cpp:183-239 — on a successful install, offer OK + PLAY with a
  // "where the missions went" message
  private onDownloadFinished(): void {
    this.downloadDone = true;
    const packid = this.downloadPackId;
    if (packid === null) {
      this.downloadStatus = 'Download complete!';
      this.d.onChange();
      return;
    }

    const pml = CreateMissionList(this.d.mlDeps, packid, kmltAll);
    if (pml === null || pml.GetCount() === 0) {
      this.downloadStatus = 'Download complete, but mission pack has no missions!';
      this.d.onChange();
      return;
    }
    let cSingle = 0,
      cMulti = 0;
    for (let i = 0; i < pml.GetCount(); i++) {
      const md = pml.GetMissionDescription(i);
      if (md === null) continue;
      if (pml.IsMultiplayerMissionType(md.mt)) cMulti++;
      else cSingle++;
    }
    const single =
      cSingle !== 0 ? `${cSingle} single player mission${cSingle === 1 ? '' : 's'} installed.` : '';
    const multi =
      cMulti !== 0 ? `${cMulti} multiplayer mission${cMulti === 1 ? '' : 's'} installed.` : '';
    this.downloadStatus = `Download Complete! ${single} ${multi}`;
    this.d.onChange();
  }

  /** true if the freshly installed pack id equals `packid` (test helper). */
  isInstalled(packid: PackId): number {
    return this.d.packm.IsInstalled(packid);
  }
  /** the in-flight download's pack id (test/view helper). */
  downloadingPackId(): PackId | null {
    return this.downloadPackId === null ? null : clonePackId(this.downloadPackId);
  }
  /** whether `key` is currently the selected row (view highlight). */
  isSelected(key: string): boolean {
    return this.selectedId !== null && this.selectedId === Number(key) >>> 0;
  }
}

export { packIdEq };
